import { CancelExecutionRequest } from '../serverApi/requests/CancelExecutionRequest';
import { GetImportManifestRequest } from '../serverApi/requests/GetImportManifestRequest';
import { PreviewImportRequest } from '../serverApi/requests/PreviewImportRequest';
import { TriggerImportRequest } from '../serverApi/requests/TriggerImportRequest';
import { IImportItem, IImportManifest } from '../serverApi/types/IBundleImport';
import { IExecution } from '../serverApi/types/IExecution';
import { requireField, sendWebRequest } from './bundleUploadActions';

/**
 * Page size for the preview and the manifest: the maximum in the service's API docs (preview-import.md,
 * import-manifest.md); the proto doesn't state one yet (W-24378181).
 */
export const IMPORT_PAGE_SIZE = 100;
/** Most preview pages the import dialog reads, so a very large bundle can't keep it loading indefinitely. */
export const MAX_PREVIEW_PAGES = 10;
/**
 * Most items `triggerImport` accepts in `selectedItemIds` (the monolith's MAX_SELECTED_IMPORT_ITEMS).
 * Items can only be picked when the whole bundle fits, so a selection never exceeds it.
 */
export const MAX_SELECTED_IMPORT_ITEMS = 1000;

/** The bundle's preview, as far as the dialog read it. */
export interface IImportPreviewData {
  readonly items: ReadonlyArray<IImportItem>;
  /** Items in the whole bundle (at least `items.length`). */
  readonly totalCount: number;
  /** False when the preview stopped at MAX_PREVIEW_PAGES with items left unlisted. */
  readonly complete: boolean;
  /** Items that will overwrite existing content, over the whole bundle when the service reports it. */
  readonly overwrittenCount: number;
  /**
   * False when unlisted items of a partial preview may overwrite too: the bundle isn't listed in full and
   * the service didn't confirm its count with a summary.
   */
  readonly overwrittenCountExact: boolean;
  /** The service sent no summary counts: the import can't start without `confirmOverwrite`. */
  readonly summaryUnavailable: boolean;
}

export function isOverwrite(item: IImportItem): boolean {
  return (item.disposition || '').toUpperCase() === 'OVERWRITTEN';
}

/** A key for the item that is unique within the preview, whether or not the service sends item ids. */
export function importItemKey(item: IImportItem, index: number): string {
  return item.itemId ? 'id:' + item.itemId : 'index:' + index;
}

/**
 * Items can be picked one by one only when every item carries an id to send back (W-24633300) and the
 * whole bundle is listed, within what `selectedItemIds` accepts; otherwise the import always covers the
 * whole bundle.
 */
export function canSelectImportItems(preview: IImportPreviewData): boolean {
  return preview.complete && preview.items.length > 0 && preview.items.length <= MAX_SELECTED_IMPORT_ITEMS
    && preview.items.every(function(item) { return !!item.itemId; });
}

/**
 * Reads the bundle's import preview page by page. Stops after MAX_PREVIEW_PAGES, or when the server
 * repeats a page token it already sent.
 */
export function loadImportPreview(bundleLuid: string): Promise<IImportPreviewData> {
  const items: IImportItem[] = [];
  const seenTokens: { [token: string]: boolean } = {};
  let totalCount = 0;
  let overwrittenCount: number | undefined;
  let summaryUnavailable = false;
  let summaryReported = false;

  const loadFrom = function(pageToken: string | undefined, pageCount: number): Promise<IImportPreviewData> {
    return sendWebRequest(new PreviewImportRequest({ bundleLuid: bundleLuid, pageSize: IMPORT_PAGE_SIZE, pageToken: pageToken }))
      .then(function(page) {
        (page.items || []).forEach(function(item) { items.push(item); });
        if (pageCount === 1) {
          totalCount = page.totalCount || 0;
          overwrittenCount = page.overwrittenCount;
          summaryUnavailable = page.hasSummary === false;
          summaryReported = page.hasSummary === true;
        }
        const next = page.nextPageToken;
        if (next && !seenTokens[next] && pageCount < MAX_PREVIEW_PAGES) {
          seenTokens[next] = true;
          return loadFrom(next, pageCount + 1);
        }
        // Fail closed: never count fewer overwrites than the listed items show, and trust a 0 for the
        // unlisted part of the bundle only when the service confirms it sent its counts.
        const listedOverwrites = items.filter(isOverwrite).length;
        const serviceOverwrites = overwrittenCount || 0;
        return {
          items: items,
          totalCount: Math.max(totalCount, items.length),
          complete: !next,
          overwrittenCount: Math.max(serviceOverwrites, listedOverwrites),
          overwrittenCountExact: !next || serviceOverwrites > 0 || summaryReported,
          summaryUnavailable: summaryUnavailable,
        };
      });
  };
  return loadFrom(undefined, 1);
}

/**
 * Queues the import of an uploaded bundle; resolves with the background job id to poll.
 * `selectedItemIds` limits it to those items (undefined imports the whole bundle).
 */
export function triggerBundleImport(bundleLuid: string, selectedItemIds: string[] | undefined, confirmOverwrite: boolean): Promise<number> {
  return sendWebRequest(new TriggerImportRequest({
    bundleLuid: bundleLuid,
    selectedItemIds: selectedItemIds,
    confirmOverwrite: confirmOverwrite,
  })).then(function(execution) { return requireField(execution && execution.backgroundJobId, 'backgroundJobId'); });
}

export function cancelImportExecution(backgroundJobId: number): Promise<IExecution> {
  return sendWebRequest(new CancelExecutionRequest({ backgroundJobId: backgroundJobId }));
}

/** One page of the import's manifest (summary counts + per-item outcomes). */
export function getImportManifest(backgroundJobId: number, pageToken?: string): Promise<IImportManifest> {
  return sendWebRequest(new GetImportManifestRequest({ backgroundJobId: backgroundJobId, pageSize: IMPORT_PAGE_SIZE, pageToken: pageToken }));
}
